// 网络优化服务 - 优化网络请求，提升加载性能
class NetworkOptimizer {
  constructor() {
    this.cache = new Map()
    this.preloadQueue = new Set()
    this.requestQueue = new Map()
    this.retryQueue = new Map()
    
    this.config = {
      cacheSize: 100,
      cacheTTL: 300000, // 5分钟
      maxRetries: 3,
      retryDelay: 1000,
      timeout: 10000,
      preloadDelay: 100,
      batchSize: 10
    }
    
    this.stats = {
      totalRequests: 0,
      cachedRequests: 0,
      failedRequests: 0,
      retriedRequests: 0,
      preloadedResources: 0,
      averageResponseTime: 0,
      cacheHitRate: 0
    }
    
    this.isOptimizing = false
    this.init()
  }
  
  // 初始化网络优化
  init() {
    if (this.isOptimizing) return
    
    this.isOptimizing = true
    this.setupRequestInterceptors()
    this.setupCacheManagement()
    this.setupPreloading()
    this.setupOfflineSupport()
    
    console.log('✅ 网络优化系统已启动')
  }
  
  // 设置请求拦截器
  setupRequestInterceptors() {
    // 拦截 fetch 请求
    const originalFetch = window.fetch
    window.fetch = async (...args) => {
      return this.optimizedFetch(originalFetch, ...args)
    }
    
    // 拦截 XMLHttpRequest
    this.setupXHRInterceptor()
  }
  
  // 优化的 fetch 请求
  async optimizedFetch(originalFetch, url, options = {}) {
    const startTime = performance.now()
    const cacheKey = this.generateCacheKey(url, options)
    
    // 检查缓存
    const cachedResponse = this.getCachedResponse(cacheKey)
    if (cachedResponse && !this.isCacheExpired(cachedResponse)) {
      this.stats.cachedRequests++
      this.stats.cacheHitRate = (this.stats.cachedRequests / this.stats.totalRequests) * 100
      return this.createResponseFromCache(cachedResponse)
    }
    
    // 添加到请求队列
    this.addToRequestQueue(cacheKey, { url, options })
    
    try {
      // 执行请求
      const response = await this.executeRequest(originalFetch, url, options)
      const endTime = performance.now()
      
      // 更新统计
      this.updateStats(endTime - startTime, true)
      
      // 缓存响应（异步，不阻塞返回）
      this.cacheResponse(cacheKey, response).catch(err => {
        console.warn('缓存响应失败:', err)
      })
      
      return response
    } catch (error) {
      // 更新统计
      this.updateStats(0, false)
      
      // 处理重试
      return this.handleRetry(cacheKey, originalFetch, url, options, error)
    }
  }
  
  // 设置 XMLHttpRequest 拦截器
  setupXHRInterceptor() {
    const originalOpen = XMLHttpRequest.prototype.open
    const originalSend = XMLHttpRequest.prototype.send
    
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._method = method
      this._url = url
      this._startTime = performance.now()
      return originalOpen.call(this, method, url, ...args)
    }
    
    XMLHttpRequest.prototype.send = function(...args) {
      const xhr = this
      const cacheKey = this.generateCacheKey(xhr._url, { method: xhr._method })
      
      // 检查缓存
      const cachedResponse = this.getCachedResponse(cacheKey)
      if (cachedResponse && !this.isCacheExpired(cachedResponse)) {
        this.stats.cachedRequests++
        this.stats.cacheHitRate = (this.stats.cachedRequests / this.stats.totalRequests) * 100
        
        // 模拟响应
        setTimeout(() => {
          xhr.readyState = 4
          xhr.status = cachedResponse.status
          xhr.responseText = cachedResponse.data
          xhr.dispatchEvent(new Event('readystatechange'))
        }, 0)
        
        return originalSend.call(this, ...args)
      }
      
      // 添加错误处理
      xhr.addEventListener('error', () => {
        this.updateStats(0, false)
        this.handleRetry(cacheKey, null, xhr._url, { method: xhr._method }, new Error('XHR Error'))
      })
      
      xhr.addEventListener('load', () => {
        const endTime = performance.now()
        this.updateStats(endTime - xhr._startTime, xhr.status < 400)
        
        if (xhr.status < 400) {
          // 为XHR创建一个Response对象进行缓存
          const response = new Response(xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: new Headers()
          })
          this.cacheResponse(cacheKey, response).catch(err => {
            console.warn('缓存XHR响应失败:', err)
          })
        }
      })
      
      return originalSend.call(this, ...args)
    }
  }
  
  // 执行请求
  async executeRequest(originalFetch, url, options) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout)
    
    try {
      const response = await originalFetch(url, {
        ...options,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }
  
  // 处理重试
  async handleRetry(cacheKey, originalFetch, url, options, error) {
    const retryCount = this.retryQueue.get(cacheKey) || 0
    
    if (retryCount < this.config.maxRetries) {
      this.retryQueue.set(cacheKey, retryCount + 1)
      this.stats.retriedRequests++
      
      // 延迟重试
      await this.delay(this.config.retryDelay * (retryCount + 1))
      
      try {
        const response = await this.executeRequest(originalFetch, url, options)
        this.retryQueue.delete(cacheKey)
        return response
      } catch (retryError) {
        return this.handleRetry(cacheKey, originalFetch, url, options, retryError)
      }
    } else {
      this.retryQueue.delete(cacheKey)
      throw error
    }
  }
  
  // 生成缓存键
  generateCacheKey(url, options) {
    const method = options.method || 'GET'
    const body = options.body ? JSON.stringify(options.body) : ''
    return `${method}:${url}:${body}`
  }
  
  // 获取缓存响应
  getCachedResponse(cacheKey) {
    return this.cache.get(cacheKey)
  }
  
  // 检查缓存是否过期
  isCacheExpired(cachedResponse) {
    return Date.now() - cachedResponse.timestamp > this.config.cacheTTL
  }
  
  // 缓存响应
  async cacheResponse(cacheKey, response) {
    if (this.cache.size >= this.config.cacheSize) {
      this.clearOldestCache()
    }
    
    // 克隆响应以避免body被消费
    const clonedResponse = response.clone()
    const responseText = await clonedResponse.text()
    
    this.cache.set(cacheKey, {
      body: responseText,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      timestamp: Date.now()
    })
  }
  
  // 从缓存创建响应
  createResponseFromCache(cachedResponse) {
    return new Response(cachedResponse.body, {
      status: cachedResponse.status,
      statusText: cachedResponse.statusText,
      headers: new Headers(cachedResponse.headers)
    })
  }
  
  // 添加到请求队列
  addToRequestQueue(cacheKey, request) {
    this.requestQueue.set(cacheKey, request)
  }
  
  // 从请求队列移除
  removeFromRequestQueue(cacheKey) {
    this.requestQueue.delete(cacheKey)
  }
  
  // 更新统计
  updateStats(responseTime, success) {
    this.stats.totalRequests++
    
    if (success) {
      this.stats.averageResponseTime = 
        (this.stats.averageResponseTime * (this.stats.totalRequests - 1) + responseTime) / 
        this.stats.totalRequests
    } else {
      this.stats.failedRequests++
    }
  }
  
  // 设置缓存管理
  setupCacheManagement() {
    // 定期清理过期缓存
    setInterval(() => {
      this.cleanupExpiredCache()
    }, 60000) // 每分钟清理一次
  }
  
  // 清理过期缓存
  cleanupExpiredCache() {
    const now = Date.now()
    const expiredKeys = []
    
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.config.cacheTTL) {
        expiredKeys.push(key)
      }
    }
    
    expiredKeys.forEach(key => {
      this.cache.delete(key)
    })
    
    if (expiredKeys.length > 0) {
      console.log(`🧹 清理了 ${expiredKeys.length} 个过期缓存`)
    }
  }
  
  // 清理最旧的缓存
  clearOldestCache() {
    let oldestKey = null
    let oldestTime = Date.now()
    
    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp
        oldestKey = key
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey)
    }
  }
  
  // 设置预加载
  setupPreloading() {
    // 预加载关键资源
    this.preloadCriticalResources()
    
    // 设置预加载观察器
    this.setupPreloadObserver()
  }
  
  // 预加载关键资源
  preloadCriticalResources() {
    const criticalResources = [
      '/src/components/Layout.jsx',
      '/src/components/AuthModal.jsx',
      '/src/components/UserProfile.jsx'
    ]
    
    criticalResources.forEach(resource => {
      this.preloadResource(resource)
    })
  }
  
  // 预加载资源
  async preloadResource(url) {
    if (this.preloadQueue.has(url)) return
    
    this.preloadQueue.add(url)
    
    try {
      const response = await fetch(url)
      if (response.ok) {
        this.stats.preloadedResources++
        console.log(`✅ 预加载成功: ${url}`)
      }
    } catch (error) {
      console.warn(`⚠️ 预加载失败: ${url}`, error)
    } finally {
      this.preloadQueue.delete(url)
    }
  }
  
  // 设置预加载观察器
  setupPreloadObserver() {
    if ('IntersectionObserver' in window) {
      const preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target
            const preloadUrl = element.dataset.preload
            
            if (preloadUrl) {
              this.preloadResource(preloadUrl)
              preloadObserver.unobserve(element)
            }
          }
        })
      }, {
        rootMargin: '200px 0px',
        threshold: 0.1
      })
      
      // 观察所有预加载元素
      document.querySelectorAll('[data-preload]').forEach(element => {
        preloadObserver.observe(element)
      })
    }
  }
  
  // 设置离线支持
  setupOfflineSupport() {
    // 监听网络状态
    window.addEventListener('online', () => {
      this.handleOnline()
    })
    
    window.addEventListener('offline', () => {
      this.handleOffline()
    })
    
    // 设置 Service Worker
    this.setupServiceWorker()
  }
  
  // 处理在线状态
  handleOnline() {
    console.log('🌐 网络已连接')
    
    // 重试失败的请求
    this.retryFailedRequests()
  }
  
  // 处理离线状态
  handleOffline() {
    console.log('📱 网络已断开')
    
    // 启用离线模式
    this.enableOfflineMode()
  }
  
  // 重试失败的请求
  retryFailedRequests() {
    const failedRequests = Array.from(this.requestQueue.entries())
    
    failedRequests.forEach(([cacheKey, request]) => {
      this.handleRetry(cacheKey, fetch, request.url, request.options, new Error('Network Error'))
    })
  }
  
  // 启用离线模式
  enableOfflineMode() {
    // 使用缓存数据
    console.log('📱 启用离线模式，使用缓存数据')
  }
  
  // 设置 Service Worker
  setupServiceWorker() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none', // 不缓存 Service Worker 文件
        scope: '/' // 明确指定作用域
      })
        .then(registration => {
          console.log('✅ Service Worker 注册成功')
          
          // 自动检查更新
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            console.log('🔄 发现 Service Worker 更新')
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                console.log('✅ Service Worker 已安装，准备激活')
                // 自动激活新的 Service Worker
                if (newWorker === registration.waiting) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' })
                }
              } else if (newWorker.state === 'activated') {
                console.log('✅ Service Worker 已激活')
              }
            })
          })
          
          // 定期检查更新（更频繁）
          setInterval(() => {
            registration.update().catch(err => {
              console.warn('Service Worker 更新检查失败:', err)
            })
          }, 30000) // 每30秒检查一次
          
          // 立即检查一次更新
          registration.update().catch(err => {
            console.warn('Service Worker 初始更新检查失败:', err)
          })
        })
        .catch(error => {
          console.warn('⚠️ Service Worker 注册失败:', error)
        })
      
      // 监听 Service Worker 控制器变化
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 Service Worker 控制器已更改，刷新页面以应用更新')
        // 延迟刷新，确保新 Service Worker 完全激活
        setTimeout(() => {
          window.location.reload()
        }, 500)
      })
    }
  }
  
  // 批量请求
  async batchRequest(requests) {
    const results = []
    const batches = this.chunkArray(requests, this.config.batchSize)
    
    for (const batch of batches) {
      const batchPromises = batch.map(request => 
        this.optimizedFetch(fetch, request.url, request.options)
      )
      
      try {
        const batchResults = await Promise.allSettled(batchPromises)
        results.push(...batchResults)
      } catch (error) {
        console.error('批量请求失败:', error)
      }
    }
    
    return results
  }
  
  // 分块数组
  chunkArray(array, size) {
    const chunks = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }
  
  // 延迟函数
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
  
  // 获取网络统计
  getNetworkStats() {
    return { ...this.stats }
  }
  
  // 获取缓存统计
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.config.cacheSize,
      hitRate: this.stats.cacheHitRate,
      ttl: this.config.cacheTTL
    }
  }
  
  // 清理缓存
  clearCache() {
    this.cache.clear()
    this.requestQueue.clear()
    this.retryQueue.clear()
    console.log('🧹 网络缓存已清理')
  }
  
  // 停止优化
  stop() {
    this.isOptimizing = false
    this.clearCache()
    console.log('⏹️ 网络优化系统已停止')
  }
  
  // 销毁优化器
  destroy() {
    this.stop()
  }
}

// 创建单例实例
const networkOptimizer = new NetworkOptimizer()

export default networkOptimizer

